import { createContext, useContext, useMemo } from "react";
import { useStoreSelector } from "./engine/createStore";
import { summaryRangeOf } from "./engine/summaryRange";
import { tilesCovering } from "./engine/tiles";

/** What the planner screen is built from: see `createPlannerEngine`. */
export const PlannerEngineContext = createContext(null);

/** The rows of the planner, as `buildRowModel` returns them. */
export const RowModelContext = createContext(null);

export function usePlannerEngine() {
  const engine = useContext(PlannerEngineContext);
  if (!engine) throw new Error("The planner engine is only available inside PlannerProvider");
  return engine;
}

export const useRowModel = () => useContext(RowModelContext);

/** Reads a part of the data, the view settings, the viewport or what the user has open. */
export const useData = (selector, isEqual) => useStoreSelector(usePlannerEngine().dataStore, selector, isEqual);
export const useView = (selector, isEqual) => useStoreSelector(usePlannerEngine().viewStore, selector, isEqual);
export const useViewport = (selector, isEqual) => useStoreSelector(usePlannerEngine().viewportStore, selector, isEqual);
export const useUi = (selector, isEqual) => useStoreSelector(usePlannerEngine().uiStore, selector, isEqual);

/**
 * The period the planner summarises (the one chosen above the left panel: a preset, the user's own days or the whole
 * life of the project) as `{ startDay, endDay }`: the hours of the rows, the number of tasks in them and the figures
 * of Resource Insights all refer to it.
 */
export function useSummaryRange() {
  const { today } = usePlannerEngine();
  const summaryPeriod = useView((state) => state.summaryPeriod);
  const customPeriod = useView((state) => state.customPeriod);
  const projectId = useUi((state) => state.projectFilter?.id);
  const project = useData((state) => (projectId ? state.data.projects[projectId] : null));

  return useMemo(() => summaryRangeOf({ summaryPeriod, customPeriod, project, today }), [summaryPeriod, customPeriod, project, today]);
}

/**
 * Whether the planner holds everything a resource has in the summary period. A long period (the whole life of a
 * project, or days far from the ones on screen) is fetched after the resource appears, and until then the hours and
 * the number of tasks would read too low.
 */
export function useSummaryReady(resourceId) {
  const { timeline } = usePlannerEngine();
  const { startDay, endDay } = useSummaryRange();
  const covered = useData((state) => state.data.allocs[resourceId]?.tiles);

  return useMemo(() => Boolean(covered) && tilesCovering(timeline, startDay, endDay).every((tile) => covered.has(tile)), [timeline, startDay, endDay, covered]);
}
